import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import Color from "@mapmycustomers/shared/enum/Color";
import MapTool from "@mapmycustomers/shared/enum/map/MapTool";
import { EntitiesSupportingGroups, Group, MapEntity } from "@mapmycustomers/shared/types/entity";
import { MultiPin } from "@mapmycustomers/shared/types/map";

import { EntityItemProperties } from "@app/component/RecordCard";
import EntityCard from "@app/component/RecordCard/components/EntityCard";
import EntityActions from "@app/scene/map/components/MapMode/components/EntityActions";
import MultiPinGroupMode from "@app/scene/map/enums/MultiPinGroupMode";
import {
  getGroup,
  getGroupEntityType,
  getGroupShowOtherPins,
} from "@app/scene/map/store/groupMode/selectors";
import {
  hideMultiPin,
  removeFromSelection,
  setPinHover,
} from "@app/scene/map/store/mapMode/actions";
import {
  getMapViewTool,
  getMultiPin,
  getMultiPinGroupMode,
  getPinHover,
  isRecordsListSortedByDistance,
} from "@app/scene/map/store/selectors";
import { showEntityView } from "@app/store/entityView/actions";
import { RootState } from "@app/store/rootReducer";
import mmcColors from "@app/styles/_colors.module.scss";
import { mapEntityIdGetter } from "@app/util/map/idGetters";

interface Props extends EntityItemProperties<EntitiesSupportingGroups> {
  group: Group;
  hideMultiPin: typeof hideMultiPin;
  multiPin?: MultiPin;
  multiPinMode?: MultiPinGroupMode;
  onRemoveFromSelection: typeof removeFromSelection;
  pinHover?: string;
  setPinHover: typeof setPinHover;
  showDistance: boolean;
  showEntityView: typeof showEntityView;
  showOtherPins: boolean;
  tool?: MapTool;
}

const GroupRecord: React.FC<Props> = ({
  entity,
  group,
  hideMultiPin,
  multiPin,
  multiPinMode,
  onRemoveFromSelection,
  pinHover,
  setPinHover,
  showDistance,
  showEntityView,
  showOtherPins,
  tool,
}) => {
  const intl = useIntl();

  const mapEntity = entity as MapEntity;

  const isHighlighted = mapEntityIdGetter(mapEntity) === pinHover;
  const isLasso = tool === MapTool.LASSO;
  const isQuickActionVisible = isHighlighted && isLasso;

  const isMultiPinOutsideRecord = !!multiPin && multiPinMode === MultiPinGroupMode.OUTSIDE_GROUP;
  const isInGroup =
    showOtherPins && isLasso
      ? Array.isArray(entity.groups) && !!entity.groups.find((gr) => gr.id === group.id)
      : true;
  const showGrayed = !isInGroup || isMultiPinOutsideRecord;

  const handleClick = useCallback(() => {
    if (!multiPin) {
      hideMultiPin();
    }

    showEntityView({
      entityId: mapEntity.id,
      entityType: mapEntity.entity,
    });
  }, [hideMultiPin, mapEntity, multiPin, showEntityView]);

  const handleHover = useCallback(() => {
    setPinHover(mapEntityIdGetter(mapEntity));
  }, [mapEntity, setPinHover]);

  const handleRemoveClick = useCallback(
    () => onRemoveFromSelection(mapEntityIdGetter(entity)),
    [entity, onRemoveFromSelection]
  );

  const removeActionHoverText = useMemo(
    () =>
      intl.formatMessage({
        id: "map.groups.removeFromSelection",
        defaultMessage: "Remove from selection",
        description: "Remove from selection quick action title",
      }),
    [intl]
  );

  return (
    <EntityCard
      borderColor={showGrayed ? mmcColors.disabledGray : group.color ?? Color.BLACK}
      entity={entity}
      extraActions={<EntityActions />}
      isHighlighted={isHighlighted}
      isRemoveActionVisible={isQuickActionVisible}
      onClick={handleClick}
      onHover={handleHover}
      onRemove={handleRemoveClick}
      removeActionHoverText={removeActionHoverText}
      showDistance={showDistance}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  entityType: getGroupEntityType(state)!, // we're pretty sure that entityType is defined since we have some record items
  group: getGroup(state)!, // same here: we're pretty sure that group is defined since we have some record items
  multiPin: getMultiPin(state),
  multiPinMode: getMultiPinGroupMode(state),
  pinHover: getPinHover(state),
  showDistance: isRecordsListSortedByDistance(state),
  showOtherPins: getGroupShowOtherPins(state),
  tool: getMapViewTool(state),
});

const mapDispatchToProps = {
  hideMultiPin,
  onRemoveFromSelection: removeFromSelection,
  setPinHover,
  showEntityView,
};

export default connect(mapStateToProps, mapDispatchToProps)(GroupRecord);
